import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Avatar,
  IconButton,
  Button,
  Chip,
  Tooltip,
  Collapse
} from '@mui/material';
import {
  Person as PersonIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  AccessTime as TimeIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon
} from '@mui/icons-material';
import dayjs from 'dayjs';

const BookingCard = ({
  booking,
  showActions = true,
  onUpdateStatus,
  onEdit,
  onDelete,
  onRejectWithReason
}) => {
  const [expanded, setExpanded] = useState(false);

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'confirmed': return 'success';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'pending': return 'En attente';
      case 'confirmed': return 'Confirmé';
      case 'cancelled': return 'Annulé';
      default: return status;
    }
  };

  const handlePhoneCall = (phoneNumber) => {
    if (phoneNumber) {
      window.location.href = `tel:${phoneNumber}`;
    }
  };

  const handleEmailSend = (email) => {
    if (email) {
      window.location.href = `mailto:${email}`;
    }
  };

  return (
    <Card
      sx={{
        mb: 0.5,
        mx: 0,
        transition: 'all 0.2s',
        border: '1px solid #f0f0f0',
        boxShadow: 'none',
        '&:hover': {
          boxShadow: 1,
          borderColor: '#e0e0e0'
        }
      }}
    >
      <CardContent sx={{ p: { xs: 1, sm: 1.5 }, '&:last-child': { pb: { xs: 1, sm: 1.5 } } }}>
        {/* Compact main row */}
        <Box display="flex" alignItems="center" gap={1}>
          {/* Avatar */}
          <Avatar sx={{
            bgcolor: 'primary.main',
            width: { xs: 28, sm: 32 },
            height: { xs: 28, sm: 32 }
          }}>
            <PersonIcon fontSize="small" />
          </Avatar>
          
          {/* Client name */}
          <Box sx={{ minWidth: 0, flex: { xs: '1 1 auto', sm: '0 0 120px' } }}>
            <Typography
              variant="body2"
              fontWeight="600"
              sx={{
                fontSize: { xs: '0.8rem', sm: '0.875rem' },
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {booking.clientName}
            </Typography>
          </Box>

          {/* Service */}
          <Box sx={{
            minWidth: 0,
            flex: { xs: '1 1 auto', sm: '0 0 140px' },
            display: { xs: 'none', sm: 'block' }
          }}>
            <Typography
              variant="body2"
              color="primary.main"
              fontWeight="600"
              sx={{
                fontSize: '0.85rem',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                backgroundColor: 'primary.light',
                color: 'primary.contrastText',
                px: 1,
                py: 0.25,
                borderRadius: 1,
                textAlign: 'center'
              }}
            >
              {booking.service}
            </Typography>
          </Box>

          {/* Date & Time */}
          <Box sx={{
            minWidth: 0,
            flex: { xs: '1 1 auto', sm: '0 0 80px' }
          }}>
            <Typography
              variant="body2"
              sx={{
                fontSize: { xs: '0.75rem', sm: '0.8rem' },
                color: 'text.secondary',
                fontWeight: 500
              }}
            >
              {dayjs(booking.date).format('DD/MM')}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontSize: { xs: '0.75rem', sm: '0.8rem' },
                color: 'text.primary',
                fontWeight: 600
              }}
            >
              {dayjs(booking.date).format('HH:mm')}
            </Typography>
          </Box>

          {/* Status */}
          <Chip
            label={getStatusLabel(booking.status)}
            color={getStatusColor(booking.status)}
            onClick={() => {}}
            size="small"
            variant="filled"
            sx={{
              fontSize: { xs: '0.65rem', sm: '0.75rem' },
              height: { xs: 20, sm: 24 },
              fontWeight: 'bold',
              cursor: 'default',
              pointerEvents: 'none',
              '&:hover': {
                backgroundColor: 'inherit'
              },
              '&:focus': {
                backgroundColor: 'inherit'
              }
            }}
          />

          {/* Quick actions */}
          <Box display="flex" gap={0.5} ml="auto">
            {booking.clientPhone && (
              <IconButton
                size="small"
                onClick={() => handlePhoneCall(booking.clientPhone)}
                sx={{
                  bgcolor: '#28a745',
                  color: 'white',
                  width: { xs: 20, sm: 24 },
                  height: { xs: 20, sm: 24 },
                  '&:hover': { bgcolor: '#218838' }
                }}
              >
                <PhoneIcon sx={{ fontSize: { xs: 10, sm: 12 } }} />
              </IconButton>
            )}

            {booking.clientEmail && (
              <IconButton
                size="small"
                onClick={() => handleEmailSend(booking.clientEmail)}
                sx={{
                  bgcolor: '#6c757d',
                  color: 'white',
                  width: { xs: 20, sm: 24 },
                  height: { xs: 20, sm: 24 },
                  '&:hover': { bgcolor: '#495057' }
                }}
              >
                <EmailIcon sx={{ fontSize: { xs: 10, sm: 12 } }} />
              </IconButton>
            )}
            
            {showActions && booking.status === 'pending' && (
              <>
                <IconButton
                  size="small"
                  onClick={() => onUpdateStatus(booking.id, 'confirmed')}
                  sx={{
                    bgcolor: '#28a745',
                    color: 'white',
                    width: { xs: 20, sm: 24 },
                    height: { xs: 20, sm: 24 },
                    '&:hover': { bgcolor: '#218838' }
                  }}
                >
                  <CheckIcon sx={{ fontSize: { xs: 10, sm: 12 } }} />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => onRejectWithReason && onRejectWithReason(booking)}
                  sx={{
                    bgcolor: '#dc3545',
                    color: 'white',
                    width: { xs: 20, sm: 24 },
                    height: { xs: 20, sm: 24 },
                    '&:hover': { bgcolor: '#c82333' }
                  }}
                >
                  <CloseIcon sx={{ fontSize: { xs: 10, sm: 12 } }} />
                </IconButton>
              </>
            )}

            {/* Expand button */}
            <IconButton
              size="small"
              onClick={() => setExpanded(!expanded)}
              sx={{
                width: { xs: 20, sm: 24 },
                height: { xs: 20, sm: 24 }
              }}
            >
              {expanded ?
                <ExpandLessIcon sx={{ fontSize: { xs: 10, sm: 12 } }} /> :
                <ExpandMoreIcon sx={{ fontSize: { xs: 10, sm: 12 } }} />
              }
            </IconButton>
          </Box>
        </Box>

        {/* Mobile service display */}
        <Box sx={{ display: { xs: 'block', sm: 'none' }, mt: 0.5 }}>
          <Typography
            variant="body2"
            color="primary.main"
            fontWeight="600"
            sx={{
              fontSize: '0.8rem',
              backgroundColor: 'primary.light',
              color: 'primary.contrastText',
              px: 1,
              py: 0.25,
              borderRadius: 1,
              display: 'inline-block'
            }}
          >
            {booking.service}
          </Typography>
        </Box>

        {/* Expanded details */}
        <Collapse in={expanded}>
          <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid #f0f0f0' }}>
            {/* Contact details */}
            <Box display="flex" flexDirection="column" gap={0.5} mb={1.5}>
              {booking.clientPhone && (
                <Box display="flex" alignItems="center" gap={1}>
                  <PhoneIcon fontSize="small" color="action" />
                  <Typography variant="body2" color="text.secondary">
                    {booking.clientPhone}
                  </Typography>
                </Box>
              )}
              {booking.clientEmail && (
                <Box display="flex" alignItems="center" gap={1}>
                  <EmailIcon fontSize="small" color="action" />
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      maxWidth: '200px'
                    }}
                  >
                    {booking.clientEmail}
                  </Typography>
                </Box>
              )}
              
              {/* Service in details */}
              <Box display="flex" alignItems="center" gap={1}>
                <Typography
                  variant="body2"
                  color="primary.main"
                  fontWeight="600"
                  sx={{
                    backgroundColor: 'primary.light',
                    color: 'primary.contrastText',
                    px: 1,
                    py: 0.25,
                    borderRadius: 1,
                    fontSize: '0.8rem'
                  }}
                >
                  Service: {booking.service}
                </Typography>
              </Box>
              
              <Box display="flex" alignItems="center" gap={1}>
                <TimeIcon fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary">
                  {dayjs(booking.date).format('DD/MM/YYYY à HH:mm')}
                </Typography>
              </Box>
            </Box>

            {/* Notes */}
            {booking.notes && (
              <Box mb={1.5} p={1} bgcolor="grey.50" borderRadius={1}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Notes:</strong> {booking.notes}
                </Typography>
              </Box>
            )}

            {/* Actions */}
            {showActions && (
              <Box display="flex" gap={1} flexWrap="wrap">
                {booking.clientEmail && (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<EmailIcon />}
                    onClick={() => handleEmailSend(booking.clientEmail)}
                    sx={{ fontSize: '0.75rem' }}
                  >
                    Email
                  </Button>
                )}
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={() => onEdit(booking)}
                  sx={{ fontSize: '0.75rem' }}
                >
                  Modifier
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={() => onDelete(booking)}
                  sx={{ fontSize: '0.75rem' }}
                >
                  Supprimer
                </Button>
              </Box>
            )}
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
};

export default BookingCard;